// TMOS Position Selector Component
// Date: December 19, 2025
// Purpose: Component for selecting business positions

import React, { useState, useEffect } from 'react';
import { businessPositionService, BusinessPosition } from '@/services/businessPositionService';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { useLanguage } from '@/state/language/LanguageProvider';

interface PositionSelectorProps {
  selectedPositionIds: string[];
  onSelectionChange: (positionIds: string[]) => void;
  primaryPositionId?: string;
  onPrimaryChange?: (positionId: string | undefined) => void;
  multiSelect?: boolean;
  showCategories?: boolean;
}

export const PositionSelector: React.FC<PositionSelectorProps> = ({
  selectedPositionIds,
  onSelectionChange,
  primaryPositionId,
  onPrimaryChange,
  multiSelect = true,
  showCategories = true
}) => {
  const { t } = useLanguage();
  const [positions, setPositions] = useState<BusinessPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    loadPositions();
  }, [selectedCategory]);

  const loadPositions = async () => {
    try {
      setLoading(true);
      const allPositions = selectedCategory === 'all' || !selectedCategory
        ? await businessPositionService.getActivePositions()
        : await businessPositionService.getPositionsByCategory(selectedCategory);
      
      setPositions(allPositions);
      
      // Extract unique categories (filter out undefined/null and ensure type safety)
      const uniqueCategories = [...new Set(allPositions.map(p => p.position_category).filter((cat): cat is string => Boolean(cat)))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error loading positions:', error);
      setPositions([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePositionToggle = (positionId: string) => {
    if (multiSelect) {
      const newSelection = selectedPositionIds.includes(positionId)
        ? selectedPositionIds.filter(id => id !== positionId)
        : [...selectedPositionIds, positionId];
      onSelectionChange(newSelection);
    } else {
      onSelectionChange([positionId]);
      if (onPrimaryChange) {
        onPrimaryChange(positionId);
      }
    }
  };

  const handlePrimaryToggle = (positionId: string) => {
    if (onPrimaryChange) {
      onPrimaryChange(primaryPositionId === positionId ? undefined : positionId);
    }
  };

  const getPositionById = (id: string) => positions.find(p => p.id === id);

  const groupedPositions = showCategories && categories.length > 0
    ? categories.reduce((acc, category) => {
        acc[category] = positions.filter(p => p.position_category === category);
        return acc;
      }, {} as Record<string, BusinessPosition[]>)
    : { 'All': positions };

  if (loading) {
    return (
      <div className="text-center py-4 text-[var(--color-secondary-text)]">
        {t('common.loading') || 'Loading positions...'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Category Filter */}
      {showCategories && categories.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
            {t('position.category') || 'Category'}
          </label>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full"
            options={[
              { value: 'all', label: t('position.allCategories') || 'All Categories' },
              ...categories.map(category => ({ value: category, label: category }))
            ]}
          />
        </div>
      )}

      {/* Position List */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {Object.entries(groupedPositions).map(([category, categoryPositions]) => (
          <div key={category}>
            {showCategories && categories.length > 0 && (
              <h4 className="text-sm font-semibold text-[var(--color-surface-text)] mb-2 mt-4 first:mt-0">
                {category}
              </h4>
            )}
            <div className="space-y-2">
              {categoryPositions.map(position => {
                const isSelected = selectedPositionIds.includes(position.id);
                const isPrimary = primaryPositionId === position.id;
                
                return (
                  <div
                    key={position.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-[var(--color-primary-bg)] border-[var(--color-primary-bg)]'
                        : 'bg-[var(--color-secondary-bg)] border-[var(--color-border)] hover:border-[var(--color-primary-bg)]'
                    }`}
                    onClick={() => handlePositionToggle(position.id)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type={multiSelect ? 'checkbox' : 'radio'}
                        checked={isSelected}
                        onChange={() => handlePositionToggle(position.id)}
                        className="w-4 h-4 text-[var(--color-primary-bg)] focus:ring-[var(--color-primary-bg)]"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[var(--color-surface-text)]">
                            {position.position_name}
                          </span>
                          {isPrimary && (
                            <Badge variant="success" className="text-xs">
                              {t('position.primary') || 'Primary'}
                            </Badge>
                          )}
                        </div>
                        {position.description && (
                          <p className="text-sm text-[var(--color-secondary-text)] mt-1">
                            {position.description}
                          </p>
                        )}
                      </div>
                    </div>
                    {multiSelect && isSelected && onPrimaryChange && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrimaryToggle(position.id);
                        }}
                        className={`ml-2 px-3 py-1 text-xs rounded ${
                          isPrimary
                            ? 'bg-[var(--color-success)] text-white'
                            : 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] hover:bg-[var(--color-primary-bg)]'
                        }`}
                      >
                        {isPrimary
                          ? t('position.setAsPrimary') || 'Primary'
                          : t('position.makePrimary') || 'Make Primary'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Selected Positions Summary */}
      {selectedPositionIds.length > 0 && (
        <div className="mt-4 p-3 bg-[var(--color-secondary-bg)] rounded-lg">
          <div className="text-sm font-medium text-[var(--color-surface-text)] mb-2">
            {t('position.selectedPositions') || 'Selected Positions'} ({selectedPositionIds.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedPositionIds.map(positionId => {
              const position = getPositionById(positionId);
              if (!position) return null;
              return (
                <Badge
                  key={positionId}
                  variant={primaryPositionId === positionId ? 'success' : 'default'}
                >
                  {position.position_name}
                  {primaryPositionId === positionId && ' (Primary)'}
                </Badge>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

